import React, { useState, useEffect } from 'react';
import { locationsApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useOfflineSync } from '../hooks/useOffline';
import { cacheManager } from '../services/cacheManager';
import { Building2, Plus, Edit, Trash2, ChevronRight, ChevronDown, MapPin, Layers, DoorOpen, FolderTree, WifiOff, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';

const TYPE_CONFIG = {
  site: { label: 'Site', icon: Building2, color: 'bg-blue-100 text-blue-700' },
  batiment: { label: 'Bâtiment', icon: Layers, color: 'bg-emerald-100 text-emerald-700' },
  etage: { label: 'Étage', icon: MapPin, color: 'bg-amber-100 text-amber-700' },
  bureau: { label: 'Bureau', icon: DoorOpen, color: 'bg-purple-100 text-purple-700' }
};

const TYPE_HIERARCHY = ['site', 'batiment', 'etage', 'bureau'];

const getNextType = (currentType) => {
  const idx = TYPE_HIERARCHY.indexOf(currentType);
  if (idx < TYPE_HIERARCHY.length - 1) {
    return TYPE_HIERARCHY[idx + 1];
  }
  return null;
};

const LocationTreeItem = ({ node, level, onEdit, onDelete, onAddChild, canManage }) => {
  const [expanded, setExpanded] = useState(level < 2);
  const config = TYPE_CONFIG[node.type_location] || TYPE_CONFIG.site;
  const Icon = config.icon;
  const nodeChildren = node.children || [];
  const hasChildren = nodeChildren.length > 0;
  const nextType = getNextType(node.type_location);
  const canAddChild = nextType !== null;
  const nextTypeLabel = nextType ? TYPE_CONFIG[nextType].label : '';
  const leftMargin = level * 24;

  return (
    <div className="select-none">
      <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors group" style={{ marginLeft: leftMargin }}>
        <button
          onClick={() => setExpanded(!expanded)}
          className={`w-6 h-6 flex items-center justify-center rounded hover:bg-slate-200 ${hasChildren ? '' : 'invisible'}`}
        >
          {hasChildren && (expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />)}
        </button>
        
        <div className={`w-8 h-8 rounded-lg ${config.color} flex items-center justify-center`}>
          <Icon className="w-4 h-4" />
        </div>
        
        <div className="flex-1 min-w-0">
          <span className="font-medium text-slate-800">{node.nom}</span>
          {node.adresse && <span className="text-xs text-slate-500 ml-2">{node.adresse}</span>}
        </div>
        
        <Badge variant="secondary" className="text-xs">{config.label}</Badge>
        
        {canManage && (
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {canAddChild && (
              <Button variant="ghost" size="sm" onClick={() => onAddChild(node, nextType)} className="h-7 px-2 text-xs">
                <Plus className="w-3 h-3 mr-1" />{nextTypeLabel}
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(node)}>
              <Edit className="w-3 h-3" />
            </Button>
            {!hasChildren && (
              <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => onDelete(node)}>
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
        )}
      </div>
      
      {expanded && hasChildren && (
        <div>
          {nodeChildren.map((child) => (
            <LocationTreeItem key={child.id} node={child} level={level + 1} onEdit={onEdit} onDelete={onDelete} onAddChild={onAddChild} canManage={canManage} />
          ))}
        </div>
      )}
    </div>
  );
};

const LocationsPage = () => {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [parentForNew, setParentForNew] = useState(null);
  const [newType, setNewType] = useState('site');
  const { isGestionnaire, isAdmin } = useAuth();
  const { isOnline } = useOfflineSync();
  const [offlineMode, setOfflineMode] = useState(false);

  const [formNom, setFormNom] = useState('');
  const [formAdresse, setFormAdresse] = useState('');
  const [formVille, setFormVille] = useState('');
  const [formDescription, setFormDescription] = useState('');

  useEffect(() => { loadTree(); }, [isOnline]);

  const loadTree = async () => {
    setLoading(true);
    try {
      const response = await locationsApi.getTree();
      setTree(response.data);
      setOfflineMode(false);
    } catch (error) {
      console.error('Erreur chargement localisations:', error);
      // Try to load complete tree from cache first
      try {
        const cachedTree = await cacheManager.getCachedData('/api/locations/tree');
        if (cachedTree && cachedTree.data && cachedTree.data.length > 0) {
          setTree(cachedTree.data);
          setOfflineMode(true);
          console.log('[LocationsPage] Loaded full tree from cache:', cachedTree.data.length, 'sites');
        } else {
          // Fallback to sites-only cache
          const cachedSites = await cacheManager.getCachedData('/api/locations/sites');
          if (cachedSites && cachedSites.data && cachedSites.data.length > 0) {
            setTree(cachedSites.data.map(site => ({ ...site, children: [] })));
            setOfflineMode(true);
            console.log('[LocationsPage] Loaded sites from cache:', cachedSites.data.length, 'sites');
          } else {
            console.error('[LocationsPage] No cached data available');
          }
        }
      } catch (cacheError) {
        console.error('[LocationsPage] Cache error:', cacheError);
      }
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = (parent, type) => {
    setSelectedLocation(null);
    setParentForNew(parent || null);
    setNewType(type || 'site');
    setFormNom('');
    setFormAdresse('');
    setFormVille('');
    setFormDescription('');
    setDialogOpen(true);
  };

  const openEditDialog = (location) => {
    setSelectedLocation(location);
    setParentForNew(null);
    setNewType(location.type_location);
    setFormNom(location.nom);
    setFormAdresse(location.adresse || '');
    setFormVille(location.ville || '');
    setFormDescription(location.description || '');
    setDialogOpen(true);
  };

  const openDeleteDialog = (location) => {
    setSelectedLocation(location);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedLocation) {
        await locationsApi.update(selectedLocation.id, {
          nom: formNom,
          adresse: formAdresse || null,
          ville: formVille || null,
          description: formDescription || null
        });
      } else {
        const parentId = parentForNew ? parentForNew.id : null;
        await locationsApi.create({
          nom: formNom,
          type_location: newType,
          parent_id: parentId,
          adresse: formAdresse || null,
          ville: formVille || null,
          description: formDescription || null
        });
      }
      setDialogOpen(false);
      loadTree();
    } catch (error) {
      console.error('Erreur:', error);
      const msg = error.response?.data?.detail || 'Erreur lors de la sauvegarde';
      alert(msg);
    }
  };

  const handleDelete = async () => {
    if (!selectedLocation) return;
    try {
      await locationsApi.delete(selectedLocation.id);
      setDeleteDialogOpen(false);
      setSelectedLocation(null);
      loadTree();
    } catch (error) {
      console.error('Erreur:', error);
      const msg = error.response?.data?.detail || 'Erreur lors de la suppression';
      alert(msg);
    }
  };

  const canManage = isGestionnaire();
  const typeLabel = TYPE_CONFIG[newType] ? TYPE_CONFIG[newType].label : 'Localisation';
  const selectedName = selectedLocation ? selectedLocation.nom : '';
  const parentName = parentForNew ? parentForNew.nom : '';

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="locations-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Localisations</h1>
            <p className="text-slate-500 mt-1">Gérez l&apos;arborescence Site → Bâtiment → Étage → Bureau</p>
          </div>
          {!isOnline && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full">
              <WifiOff className="w-4 h-4 text-amber-600" />
              <span className="text-sm font-medium text-amber-700">Hors-ligne</span>
            </div>
          )}
        </div>
        {canManage && !offlineMode && (
          <Button onClick={() => openCreateDialog(null, 'site')} className="bg-blue-600 hover:bg-blue-700 btn-scale" data-testid="add-site-btn">
            <Plus className="w-4 h-4 mr-2" />Nouveau site
          </Button>
        )}
      </div>

      {offlineMode && (
        <Alert className="bg-amber-50 border-amber-200">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <strong>Mode hors-ligne :</strong> Seuls les sites pré-chargés sont visibles. La hiérarchie complète et les modifications nécessitent une connexion Internet.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            {Object.entries(TYPE_CONFIG).map(([key, cfg]) => {
              const Ic = cfg.icon;
              return (
                <div key={key} className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded ${cfg.color} flex items-center justify-center`}><Ic className="w-3 h-3" /></div>
                  <span className="text-sm text-slate-600">{cfg.label}</span>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <FolderTree className="w-5 h-5 text-slate-400" />Arborescence des localisations
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tree.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">Aucune localisation</h3>
              <p className="text-slate-500 mt-1">Commencez par créer votre premier site</p>
              {canManage && !offlineMode && (
                <Button onClick={() => openCreateDialog(null, 'site')} className="mt-4 bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />Créer un site
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-1">
              {tree.map((node) => (
                <LocationTreeItem 
                  key={node.id} 
                  node={node} 
                  level={0} 
                  onEdit={offlineMode ? null : openEditDialog} 
                  onDelete={offlineMode ? null : openDeleteDialog} 
                  onAddChild={offlineMode ? null : openCreateDialog} 
                  canManage={canManage && !offlineMode} 
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedLocation ? `Modifier ${selectedName}` : `Nouveau ${typeLabel}`}</DialogTitle>
            {parentName && <p className="text-sm text-slate-500">Dans: {parentName}</p>}
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nom">Nom *</Label>
              <Input id="nom" value={formNom} onChange={(e) => setFormNom(e.target.value)} placeholder="Nom de la localisation" required data-testid="input-location-name" />
            </div>
            {newType === 'site' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="adresse">Adresse</Label>
                  <Input id="adresse" value={formAdresse} onChange={(e) => setFormAdresse(e.target.value)} placeholder="123 rue Example" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ville">Ville</Label>
                  <Input id="ville" value={formVille} onChange={(e) => setFormVille(e.target.value)} placeholder="Paris" />
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input id="description" value={formDescription} onChange={(e) => setFormDescription(e.target.value)} placeholder="Description optionnelle" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="save-location-btn">{selectedLocation ? 'Mettre à jour' : 'Créer'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>Êtes-vous sûr de vouloir supprimer {selectedName} ? Cette action est irréversible.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-location">Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LocationsPage;
